import {stageDisplayName} from './stage.js';

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function resultStatus(isOnline){
  return isOnline()?'saved_pending_sync':'saved_offline';
}

function targetPayload(row){
  if(!row)return null;
  return Object.freeze({
    targetChamberCelsius:row.targetChamberCelsius??null,
    minChamberCelsius:row.minChamberCelsius??null,
    maxChamberCelsius:row.maxChamberCelsius??null,
    plannedDurationMinutes:row.plannedDurationMinutes??null
  });
}

export function createSmokingStageEditor({
  stageStore,
  targetStore,
  sessionId,
  sessionStatus='draft',
  items=[],
  locale='pl',
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(!stageStore?.create||!stageStore?.get||!stageStore?.list||!stageStore?.update||!stageStore?.remove||!stageStore?.reorder){
    throw new TypeError('Smoking stage store is required');
  }
  if(!targetStore?.getStageTarget||!targetStore?.saveStageTarget||!targetStore?.clearStageTarget
    ||!targetStore?.listItemTargets||!targetStore?.saveItemTarget||!targetStore?.clearItemTarget){
    throw new TypeError('Stage target store is required');
  }

  const session=required(sessionId,'sessionId');
  let currentStatus=String(sessionStatus||'draft').trim().toLowerCase();
  let currentItems=Object.freeze([...(items||[])]);
  let stages=Object.freeze([]);
  let summaries=Object.freeze([]);
  let editingId=null;
  let editingTarget=null;
  let editingItemTargets=Object.freeze([]);

  async function stageSummary(stage){
    const [target,itemTargets]=await Promise.all([
      targetStore.getStageTarget(stage.id),
      targetStore.listItemTargets(stage.id)
    ]);
    return Object.freeze({
      stage,
      displayName:stageDisplayName(stage,{locale}),
      target:targetPayload(target),
      itemTargetCount:itemTargets.length
    });
  }

  async function refresh(){
    stages=await stageStore.list(session);
    summaries=Object.freeze(await Promise.all(stages.map(stageSummary)));
    if(editingId&&!stages.some(stage=>stage.id===editingId)){
      editingId=null;
      editingTarget=null;
      editingItemTargets=Object.freeze([]);
    }
    return snapshot();
  }

  function snapshot(){
    return Object.freeze({
      sessionId:session,
      sessionStatus:currentStatus,
      items:currentItems,
      stages,
      summaries,
      editingId,
      editingStage:editingId?stages.find(stage=>stage.id===editingId)||null:null,
      editingTarget,
      editingItemTargets,
      offline:!isOnline()
    });
  }

  function setSessionStatus(value){
    currentStatus=String(value||'').trim().toLowerCase();
    return snapshot();
  }

  function setItems(next=[]){
    currentItems=Object.freeze([...(next||[])]);
    return snapshot();
  }

  function beginCreate(){
    editingId=null;
    editingTarget=null;
    editingItemTargets=Object.freeze([]);
    return snapshot();
  }

  async function beginEdit(id){
    const stage=await stageStore.get(required(id,'id'));
    if(!stage||stage.sessionId!==session)throw new Error('SMOKING_STAGE_NOT_FOUND');
    const [target,itemTargets]=await Promise.all([
      targetStore.getStageTarget(stage.id),
      targetStore.listItemTargets(stage.id)
    ]);
    editingId=stage.id;
    editingTarget=targetPayload(target);
    editingItemTargets=Object.freeze(itemTargets.map(row=>Object.freeze({
      itemId:row.itemId,
      targetInternalCelsius:row.targetInternalCelsius
    })));
    return snapshot();
  }

  async function persistTargets(stageId,input){
    if(input.targetEmpty){
      await targetStore.clearStageTarget(stageId,{sessionStatus:currentStatus});
    }else{
      await targetStore.saveStageTarget(stageId,input.target,{sessionStatus:currentStatus});
    }

    const existing=await targetStore.listItemTargets(stageId);
    const desired=new Map((input.itemTargets||[]).map(row=>[row.itemId,row.targetInternalCelsius]));

    for(const row of existing){
      if(!desired.has(row.itemId)){
        await targetStore.clearItemTarget(stageId,row.itemId,{sessionStatus:currentStatus});
      }
    }
    for(const [itemId,targetInternalCelsius] of desired){
      await targetStore.saveItemTarget(
        stageId,
        itemId,
        {targetInternalCelsius},
        {sessionStatus:currentStatus}
      );
    }
  }

  async function save(input={}){
    if(!input?.stage||!('targetEmpty' in input)||!Array.isArray(input.itemTargets)){
      throw new TypeError('Validated stage editor input is required');
    }
    const saved=editingId
      ?await stageStore.update(editingId,input.stage,{sessionStatus:currentStatus})
      :await stageStore.create(session,input.stage,{sessionStatus:currentStatus});

    await persistTargets(saved.id,input);
    editingId=null;
    editingTarget=null;
    editingItemTargets=Object.freeze([]);
    await refresh();
    return Object.freeze({ok:true,status:resultStatus(isOnline),saved,state:snapshot()});
  }

  async function requestRemove(id){
    const stage=await stageStore.get(required(id,'id'));
    if(!stage||stage.sessionId!==session)throw new Error('SMOKING_STAGE_NOT_FOUND');
    const [target,itemTargets]=await Promise.all([
      targetStore.getStageTarget(stage.id),
      targetStore.listItemTargets(stage.id)
    ]);
    return Object.freeze({
      ok:false,
      status:'confirmation_required',
      stageId:stage.id,
      displayName:stageDisplayName(stage,{locale}),
      hasTargetData:Boolean(target)||itemTargets.length>0,
      itemTargetCount:itemTargets.length
    });
  }

  async function remove(id,{confirmed=false}={}){
    const plan=await requestRemove(id);
    if(!confirmed)return plan;

    const itemTargets=await targetStore.listItemTargets(plan.stageId);
    for(const row of itemTargets){
      await targetStore.clearItemTarget(plan.stageId,row.itemId,{sessionStatus:currentStatus});
    }
    await targetStore.clearStageTarget(plan.stageId,{sessionStatus:currentStatus});
    const saved=await stageStore.remove(plan.stageId,{sessionStatus:currentStatus});

    if(editingId===plan.stageId)beginCreate();
    await refresh();
    return Object.freeze({ok:true,status:resultStatus(isOnline),saved,state:snapshot()});
  }

  async function move(id,direction){
    const ordered=[...stages];
    const index=ordered.findIndex(stage=>stage.id===id);
    if(index<0)throw new Error('SMOKING_STAGE_NOT_FOUND');
    const delta=direction==='up'?-1:direction==='down'?1:0;
    if(!delta)throw new TypeError('direction must be up or down');
    const target=index+delta;
    if(target<0||target>=ordered.length)return Object.freeze({ok:true,status:'unchanged',state:snapshot()});
    [ordered[index],ordered[target]]=[ordered[target],ordered[index]];
    await stageStore.reorder(session,ordered.map(stage=>stage.id),{sessionStatus:currentStatus});
    await refresh();
    return Object.freeze({ok:true,status:resultStatus(isOnline),state:snapshot()});
  }

  return Object.freeze({
    refresh,snapshot,setSessionStatus,setItems,beginCreate,beginEdit,save,requestRemove,remove,move
  });
}
